import { composeProgress } from "apollo/utils";
import {
  CLINICAL_CONSULTATION_ACTION,
  SEARCH_ACTION_DELETE_FORM,
} from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFormName,
  Patient,
} from "core/types";
import { useUpdateAuctionFormsCache } from "dsl/ecosystems/PatientFiles/useUpdateAuctionFormsCache";
import { useClinicalConsultationAction } from "dsl/hooks";
import useSearchAction from "dsl/hooks/useSearchAction";
import { useCallback } from "react";
import { FORM_NAME_TO_FILE_TYPE } from "./utils";

export function useFormDelete({
  auction,
  consultation,
  form,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  formName: DocsFormName;
  form: AuctionForm | null | undefined;
  patient: Patient;
}) {
  const [deleteAuctionForm, deleteAuctionFormProgress] = useSearchAction({
    actionType: SEARCH_ACTION_DELETE_FORM,
  });

  const [deleteFormConsultation, deleteFormConsultationProgress] =
    useClinicalConsultationAction({
      actionType: CLINICAL_CONSULTATION_ACTION.DELETE_FORM,
    });

  const updateAuctionFormsCache = useUpdateAuctionFormsCache();

  const onDelete = useCallback(async (): Promise<boolean> => {
    try {
      if (consultation) {
        if (!form?.id || !form.form_type) {
          console.error("form id or form type is not defined");
          return false;
        }
        const deleted = await deleteFormConsultation({
          consultation,
          context: {
            form_type: form.form_type,
            id: form.id,
            without_file_side_effects: true,
          },
          patient,
        });
        return !!deleted;
      }
      if (!auction) {
        console.error("auction is not defined");
        return false;
      }
      const deleted = await deleteAuctionForm({
        auction,
        context: {
          form_type: FORM_NAME_TO_FILE_TYPE[formName],
          without_file_side_effects: true,
        },
        onCompleted: updateAuctionFormsCache,
      });

      return !!deleted;
    } catch (err) {
      console.error(`error deleting ${formName} form: ${getErrorMessage(err)}`);
      return false;
    }
  }, [
    auction,
    consultation,
    deleteAuctionForm,
    deleteFormConsultation,
    form,
    formName,
    patient,
    updateAuctionFormsCache,
  ]);

  return {
    deleteFormProgress: composeProgress([
      deleteAuctionFormProgress,
      deleteFormConsultationProgress,
    ]),
    onDelete,
  };
}
